// src/services/authService.js
import {auth,db} from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from "firebase/auth";
import {
  // Existing imports...
  GoogleAuthProvider, // NEW
  signInWithPopup,    // NEW
  // ...
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp
} from "firebase/firestore";

/**
 * SIGNUP (replaces POST /api/auth/signup)
 * - creates Firebase Auth user
 * - creates Firestore user profile (users/{uid})
 */
export async function signup(name,email,password){
  if(!name || !email || !password){
    throw new Error("Name, email, and password are required");
  }

  // Create auth user
  const cred=await createUserWithEmailAndPassword(auth,email,password);
  const user=cred.user;

  // Create user profile doc in Firestore
  const userRef=doc(db,"users",user.uid);
  await setDoc(userRef,{
    name:name.trim(),
    email:user.email.toLowerCase(),
    createdAt:serverTimestamp(),
    updatedAt:serverTimestamp()
  });

  return {
    id:user.uid,
    name:name.trim(),
    email:user.email
  };
}

/**
 * LOGIN (replaces POST /api/auth/login)
 */
export async function login(email,password){
  if(!email || !password){
    throw new Error("Email and password are required");
  }

  const cred=await signInWithEmailAndPassword(auth,email,password);
  const user=cred.user;

  // Load profile from Firestore
  const userRef=doc(db,"users",user.uid);
  const snap=await getDoc(userRef);

  const profile=snap.exists()
    ? snap.data()
    : { name:user.email, email:user.email };

  return {
    id:user.uid,
    name:profile.name,
    email:profile.email
  };
}

/**
 * LOGOUT (replaces POST /api/auth/logout)
 */
export function logout(){
  return signOut(auth);
}

/**
 * ME (replaces GET /api/auth/me)
 * - returns current user's Firestore profile
 */
export async function getCurrentUserProfile(){
  const user=auth.currentUser;
  if(!user) throw new Error("Not authenticated");

  const userRef=doc(db,"users",user.uid);
  const snap=await getDoc(userRef);

  if(!snap.exists()){
    throw new Error("User not found");
  }

  return {
    id:user.uid,
    ...snap.data()
  };
}

/**
 * Subscribe to auth state changes
 * (similar to checking session on page load)
 * 
 * Usage:
 * const unsubscribe = subscribeToAuthChanges(user => { ... });
 */
export function subscribeToAuthChanges(callback){
  return onAuthStateChanged(auth,callback); // callback(user | null)
}

/**
 * SIGN IN WITH GOOGLE
 * - Authenticates with Google popup
 * - Creates/gets Firestore user profile
 */
const googleProvider = new GoogleAuthProvider();
export async function signInWithGoogle() {
  const cred = await signInWithPopup(auth, googleProvider);
  const user = cred.user;

  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);

  if (!snap.exists()) {
    // If user is signing up for the first time, create their profile
    await setDoc(userRef, {
      name: user.displayName || user.email.split('@')[0],
      email: user.email.toLowerCase(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      id: user.uid,
      name: user.displayName,
      email: user.email,
    };
  } else {
    // If user already exists, load their profile
    const profile = snap.data();
    return {
      id: user.uid,
      name: profile.name,
      email: profile.email,
    };
  }
}